'use-strict';
import React, { useEffect, useState } from 'react';
import intersection from 'lodash/intersection';
import flatten from 'lodash/flatten';
import map from 'lodash/map';
import { useHistory } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useCurrentUserQuery } from '@/redux/services/portal/user.api.js';
import { useLoadingState } from '@/contexts/LoadingContext';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useLogout } from '@/contexts/LogoutContext';
import * as config from '@/config/env.config';

/**
 * HOC that Handles whether or not the user is allowed to see the page.
 */
export const RouteAuthorization = ({
  allowedPermissions,
  WrappedComponent,
  ...props
}) => {
	const history = useHistory();
	const isAuthenticated = useIsAuthenticated();
	const { isLoading: auth0IsLoading } = useAuth0();
	const { isLoggingOut } = useLogout();
	const [shouldRedirect, setShouldRedirect] = useState(false);
	const [redirectReason, setRedirectReason] = useState(null);
	
	// Fetch user data only if authenticated (trust Auth0 SDK state as source of truth)
	const { data: user, isLoading: userIsLoading, error } = useCurrentUserQuery('', {
		skip: !isAuthenticated || auth0IsLoading
	});
	
	// Register loading state with global loading context
	useLoadingState('route-authorization', userIsLoading || auth0IsLoading);
	
	// Schedule redirects when auth/user fetch fails (avoid setState during render)
	useEffect(() => {
		if (isLoggingOut || shouldRedirect) {
			return;
		}

		if (error && isAuthenticated) {
			setRedirectReason('Error: ' + (error.message || error));
			setShouldRedirect(true);
			return;
		}

		if (isAuthenticated && !user && !userIsLoading) {
			setRedirectReason('Authenticated but no user data');
			setShouldRedirect(true);
			return;
		}

		if (!user && !isAuthenticated) {
			setRedirectReason('User is not authenticated');
			setShouldRedirect(true);
		}
	}, [
		isLoggingOut,
		shouldRedirect,
		error,
		isAuthenticated,
		user,
		userIsLoading,
	]);
	
	// Handle redirects (must be in useEffect, not render)
	useEffect(() => {
		if (isLoggingOut) {
			return;
		}

		if (shouldRedirect && redirectReason) {
			const shouldRedirectToLogin =
				!isAuthenticated && redirectReason === 'User is not authenticated';
			const redirectPath = shouldRedirectToLogin ? '/login' : '/securitycheck';
			const redirectInfo = {
				reason: redirectReason,
				redirectPath,
				pathname: props.location.pathname,
				isAuthenticated,
				user: user ? { id: user.id, email: user.email, roles: user.roles?.length } : null,
				error: error ? { message: error.message, status: error.status } : null
			};
			
			if (config.ENABLE_SECURITY_ALERT) {
				alert('About to redirect to ' + redirectPath + '\n\nState:\n' + JSON.stringify(redirectInfo, null, 2) + '\n\nCheck console and state before clicking OK');
			}
			
			history.replace({
				pathname: redirectPath,
				state: { from: props.location }
			});
		}
	}, [isLoggingOut, shouldRedirect, redirectReason, history, props.location, isAuthenticated, user, error]);
	
	// Show loading while Auth0 or user data is being fetched
	if (auth0IsLoading || userIsLoading) {
		return null; // GlobalLoading will handle the spinner
	}

	if (isLoggingOut) {
		return null;
	}
  
	if (shouldRedirect) {
		return null;
	}

	if (!user && isAuthenticated) {
		return null;
	}

  if (!user) {
    return null;
  }
  
  if (!user.roles) {
    return <h1>Access Permissions Not Found!</h1>;
  }
  const permissionArray = flatten(map(user.roles, 'permissions'));

  if (intersection(allowedPermissions, permissionArray).length > 0 ) {
    return <WrappedComponent {...props} />;
  } else {
    return <h1>Access Permissions Not Found</h1>;
  }
};

export default RouteAuthorization;
